/**
 * The engine's plugins - how a condition compares and how an effect changes state. The backend twin
 * is services/game_builder/mechanics/core.py; both engines answer the shared cases in
 * backend/game_builder/tests/fixtures/engine-conformance.json (engineConformance.test.js), so a
 * compare or effect op added here must be added there too.
 */
import { createRegistry } from './registry';

const clone = (v) => JSON.parse(JSON.stringify(v));

// ---- compare: {item, prop, op, value}
export const COMPARES = createRegistry('compare op');
// Equality as JSON values (null, bools, numbers, strings, lists): true is not 1 - same as the backend.
const same = (actual, value) => JSON.stringify(actual ?? null) === JSON.stringify(value ?? null);
const numeric = (fn) => (actual, value) => {
  const a = Number(actual);
  const v = Number(value);
  if (actual === null || actual === undefined || Number.isNaN(a) || Number.isNaN(v)) return false;
  return fn(a, v);
};
COMPARES.register({ id: 'eq', symbol: '=', test: same });
COMPARES.register({ id: 'ne', symbol: '≠', test: (actual, value) => !same(actual, value) });
COMPARES.register({ id: 'gt', symbol: '>', test: numeric((a, v) => a > v) });
COMPARES.register({ id: 'lt', symbol: '<', test: numeric((a, v) => a < v) });
COMPARES.register({ id: 'gte', symbol: '≥', test: numeric((a, v) => a >= v) });
COMPARES.register({ id: 'lte', symbol: '≤', test: numeric((a, v) => a <= v) });

// ---- effects: {op, item, prop, value} - apply(props, prop, value) changes the item's props in place
export const EFFECTS = createRegistry('effect op');
EFFECTS.register({ id: 'set', apply: (props, prop, value) => { props[prop] = clone(value); } });
EFFECTS.register({ id: 'add', apply: (props, prop, value) => { props[prop] = (props[prop] || 0) + value; } });
EFFECTS.register({ id: 'append', apply: (props, prop, value) => { props[prop] = [...(props[prop] || []), clone(value)]; } });

// ---- state the player can SEE (backend SHOWN_PROPS): a problem must read one of these to show in the scene
export const SHOWN_PROPS = createRegistry('shown prop');
['hidden', 'blocked_by', 'open', 'on', 'locked', 'armed', 'ringing', 'signatures', 'mood', 'zone', 'value', 'amount', 'entries']
  .forEach((id) => SHOWN_PROPS.register({ id }));

// ---- reactions: the bubble over someone (backend REACTIONS)
export const REACTIONS = createRegistry('reaction');
[['angry', '😠'], ['happy', '🙂'], ['nod', '👍'], ['alarm', '🚨'], ['calm', '😌']].forEach(([id, emoji]) => REACTIONS.register({ id, emoji }));
